import { useState } from "react";
import { useSegment } from "@/hooks/use-segment";
import { useReportsContext } from "../../contexts/reports-context";
import { AssetRenderDropdown } from "./asset-render-dropdown";
import { CustomDropdownItem } from "@/components/ui/dropdown";
import { Checkbox } from "@/components/ui/form";
import { getImageUrl, shouldShowVisualisationDropdown, getLayerControls, type LayerState } from "../../utils/asset-render-utils";
import { Visualisation } from "@/api/renderer/types/renderer.type";

/* Prop Types */
interface AssetRenderProps {
  projectId: string;
  studyId: string;
}

/*
 * AssetRender component
 * Renders the asset with the selected KPI and any additional layer options
 */
export const AssetRender = ({ projectId, studyId }: AssetRenderProps) => {
  /* State */
  const [visualisation, setVisualisation] = useState<Visualisation>("none");
  const [layers, setLayers] = useState<LayerState>({
    showHotspots: true,
    showGazePath: false,
    showAOIRank: false,
  });

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Context */
  const {
    state: { assetKey, contextKey, reportType, attentionMetric, attentionSubMetric, memoryMetric, emotionMetric },
  } = useReportsContext();

  /* Handlers */
  const handleVisualisationChange = (newVisualisation: Visualisation) => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Visualisation Changed", {
      assetId: assetKey,
      projectId,
      studyId,
      reportType: reportType,
      from: visualisation,
      to: newVisualisation,
      studyType: "asset",
    });
    setVisualisation(newVisualisation);
  };

  const handleHotspotsChange = () => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Layer Toggled", {
      assetId: assetKey,
      projectId,
      studyId,
      reportType: reportType,
      layer: "hotspots",
      value: !layers.showHotspots,
      studyType: "asset",
    });

    // toggle the hotspots layer
    setLayers({ ...layers, showHotspots: !layers.showHotspots });
  };

  const handleGazePathChange = () => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Layer Toggled", {
      assetId: assetKey,
      projectId,
      studyId,
      reportType: reportType,
      layer: "gazePath",
      value: !layers.showGazePath,
      studyType: "asset",
    });

    // toggle the gaze path layer
    setLayers({ ...layers, showGazePath: !layers.showGazePath });
  };

  const handleAOIRankChange = () => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Layer Toggled", {
      assetId: assetKey,
      projectId,
      studyId,
      reportType: reportType,
      layer: "aoiRank",
      value: !layers.showAOIRank,
      studyType: "asset",
    });

    // toggle the AOI rank layer
    setLayers({ ...layers, showAOIRank: !layers.showAOIRank });
  };

  /* Computed Values */
  const imageUrl = getImageUrl(reportType, attentionMetric, visualisation, layers, projectId, studyId, assetKey, contextKey, attentionSubMetric, memoryMetric, emotionMetric);
  const showDropdown = shouldShowVisualisationDropdown(reportType, attentionMetric);
  const layerControlType = getLayerControls(reportType, attentionMetric);

  /* Render */
  return (
    <div className="grow flex flex-row bg-base-300 rounded-lg overflow-hidden max-h-full relative">
      {/* Visualisation Dropdown */}
      {showDropdown && (
        <AssetRenderDropdown visualisation={visualisation} handleVisualisationChange={handleVisualisationChange}>
          {/* Layer Controls for Clarity */}
          {layerControlType === "clarity" && (
            <>
              <p className="px-2 text-sm">Layers</p>
              <CustomDropdownItem className="p-0 flex" onClick={handleHotspotsChange}>
                <div className="form-control grow px-4 py-2">
                  <Checkbox checked={layers.showHotspots} size={"sm"} className="pointer-events-none" label="Hotspots" readOnly />
                </div>
              </CustomDropdownItem>
              <CustomDropdownItem className="p-0 flex" onClick={handleGazePathChange}>
                <div className="form-control grow px-4 py-2">
                  <Checkbox checked={layers.showGazePath} size={"sm"} className="pointer-events-none" label="Gaze path" readOnly />
                </div>
              </CustomDropdownItem>
            </>
          )}

          {/* Layer Controls for Digestibility */}
          {layerControlType === "digestibility" && (
            <>
              <p className="px-2 text-sm">Layer</p>
              <CustomDropdownItem className="p-0 flex" onClick={handleAOIRankChange}>
                <div className="form-control grow px-4 py-2">
                  <Checkbox checked={layers.showAOIRank} size={"sm"} className="pointer-events-none" label="AOI rank" readOnly />
                </div>
              </CustomDropdownItem>
            </>
          )}
        </AssetRenderDropdown>
      )}

      {/* Image Render */}
      <img src={imageUrl} alt={`${reportType} report`} className="w-full h-full object-contain" />
    </div>
  );
};
